import { Close, HelpOutline, OpenInFull } from '@mui/icons-material';
import { Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  title: string;
  widgetId: string;
  expandable?: boolean;
  closable?: boolean;
  onClose: (widgetId: string) => void;
  onStartTour?: () => void;
  children: React.ReactNode;
}

export const DashboardSidebarWidgetCard: FC<Props> = ({
  title,
  widgetId,
  expandable = false,
  closable = true,
  onClose,
  onStartTour,
  children,
}: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleExpand = (): void => {
    setExpanded(true);
  };

  const handleCollapse = (): void => {
    setExpanded(false);
  };

  return (
    <>
      <section
        className={`relative flex min-h-[220px] w-full shrink-0 flex-col rounded-lg border border-solid border-gray-200 bg-white shadow-sm`}
        data-dashboard-tour="sidebar-widget-card"
        data-dashboard-tour-widget={widgetId}
      >
        <div
          className={`flex min-h-[48px] items-center justify-between border-0 border-b border-solid border-gray-200 px-4 ${
            closable ? 'pr-28' : 'pr-20'
          }`}
        >
          <Typography
            variant="subtitle1"
            className="font-medium"
            data-dashboard-tour="sidebar-widget-title"
          >
            {title}
          </Typography>
        </div>
        <div className="absolute right-2 top-2 flex items-center gap-1">
          {onStartTour && (
            <IconButton
              size="small"
              aria-label={t('dashboard_start_named_guide', 'Start {{title}} guide', { title })}
              data-dashboard-tour="sidebar-widget-help"
              onClick={onStartTour}
            >
              <HelpOutline fontSize="small" />
            </IconButton>
          )}
          {expandable && (
            <Tooltip title={t('dashboard_expand_named', 'Expand {{title}}', { title })}>
              <IconButton
                size="small"
                aria-label={t('dashboard_expand_named_widget', 'Expand {{title}} widget', {
                  title,
                })}
                data-dashboard-tour="sidebar-widget-expand"
                onClick={handleExpand}
              >
                <OpenInFull fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {closable && (
            <Tooltip title={t('dashboard_close_named', 'Close {{title}}', { title })}>
              <IconButton
                size="small"
                aria-label={t('dashboard_close_named_widget', 'Close {{title}} widget', {
                  title,
                })}
                data-dashboard-tour="sidebar-widget-close"
                onClick={(): void => onClose(widgetId)}
              >
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {expanded ? (
          <div className="px-4 py-6 text-sm text-gray-600">
            {t('dashboard_dialog_currently_open', 'Dialog is currently open.')}
          </div>
        ) : (
          <div className="min-w-0 overflow-visible">{children}</div>
        )}
      </section>

      <Dialog
        open={expanded}
        disablePortal
        onClose={handleCollapse}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            width: 'min(56rem, calc(100vw - 2rem))',
            maxWidth: 'none',
          },
        }}
      >
        <DialogTitle className="flex items-center justify-between pr-12">
          <Typography variant="h6">{title}</Typography>
          <IconButton
            size="small"
            aria-label={t(
              'dashboard_close_expanded_named_widget',
              'Close expanded {{title}} widget',
              { title }
            )}
            className="absolute right-3 top-3"
            onClick={handleCollapse}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="p-0">
          {expanded && (
            <div className="min-w-0 overflow-visible" data-dashboard-tour-widget={widgetId}>
              {children}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
